<template>
  <section class="comments" aria-labelledby="comments-title">
    <div class="comments__inner">
      <p class="comments__kicker">Keep the Story Going</p>
      <div class="comments__heading">
        <div>
          <h2 id="comments-title">The Conversation</h2>
          <p>Share a memory, add a detail, or reply to the crew.</p>
        </div>
        <span v-if="comments.length" class="comments__count">
          {{ comments.length }} {{ comments.length === 1 ? 'comment' : 'comments' }}
        </span>
      </div>

      <div v-if="loadError" class="comments__notice comments__notice--error" role="alert">
        {{ loadError }}
      </div>

      <div class="comments__composer">
        <template v-if="user">
          <div class="comments__identity">
            <span>Commenting as <strong>{{ user.displayName || user.email }}</strong></span>
            <button type="button" @click="signOutUser">Sign out</button>
          </div>
          <div v-if="replyTarget" class="comments__replying">
            Replying to {{ replyTarget.authorName }}
            <button type="button" aria-label="Cancel reply" @click="cancelReply">×</button>
          </div>
          <textarea
            id="comment-body"
            v-model="draft"
            aria-label="Your comment"
            maxlength="1200"
            :placeholder="replyTarget ? 'Write a reply…' : 'Add to the story…'"
            rows="4"
            @keydown.ctrl.enter="submitComment"
            @keydown.meta.enter="submitComment"
          ></textarea>
          <div class="comments__actions">
            <small>{{ draft.length }}/1200</small>
            <button
              type="button"
              class="comments__submit"
              :disabled="!draft.trim() || submitting"
              @click="submitComment"
            >
              {{ submitting ? 'Posting…' : (replyTarget ? 'Post Reply' : 'Post Comment') }}
            </button>
          </div>
        </template>
        <template v-else>
          <p>Sign in so the crew knows who is joining the conversation.</p>
          <button
            type="button"
            class="comments__signin"
            :disabled="authenticating || !authReady"
            @click="signIn"
          >
            {{ authenticating ? 'Opening Google…' : 'Continue with Google' }}
          </button>
        </template>
      </div>

      <p v-if="submitError" class="comments__notice comments__notice--error" role="alert">
        {{ submitError }}
      </p>

      <div v-if="loading" class="comments__notice" aria-live="polite">Loading the conversation…</div>
      <div v-else-if="!loadError && !threads.length" class="comments__empty">
        <strong>First one here?</strong>
        <span>Leave a note and get the conversation started.</span>
      </div>
      <div v-else class="comments__threads">
        <comment-thread-item
          v-for="comment in threads"
          :key="comment.id"
          :comment="comment"
          :page-id="pageId"
          @reply="startReply"
        />
      </div>
    </div>
  </section>
</template>

<script>
import {
  addDoc,
  collection,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/firebase';
import CommentThreadItem from './CommentThreadItem.vue';

export default {
  name: 'ThreadedComments',
  components: { CommentThreadItem },
  props: {
    pageKey: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    authenticating: false,
    comments: [],
    draft: '',
    loadError: '',
    loading: true,
    replyTarget: null,
    submitError: '',
    submitting: false,
    unsubscribeComments: null,
  }),
  computed: {
    authError() {
      return this.$store.getters.authError;
    },
    authReady() {
      return this.$store.getters.authReady;
    },
    pageId() {
      const normalizedPath = this.pageKey.replace(/\/$/, '') || '/';
      return encodeURIComponent(normalizedPath);
    },
    threads() {
      const byId = new Map(this.comments.map((comment) => [comment.id, {
        ...comment,
        children: [],
      }]));
      const roots = [];

      byId.forEach((comment) => {
        const parent = comment.parentId && byId.get(comment.parentId);
        if (parent) parent.children.push(comment);
        else roots.push(comment);
      });

      return roots;
    },
    user() {
      return this.$store.getters.currentUser;
    },
  },
  watch: {
    pageId() {
      this.cancelReply();
      this.subscribeToComments();
    },
  },
  created() {
    this.subscribeToComments();
  },
  beforeDestroy() {
    if (this.unsubscribeComments) this.unsubscribeComments();
  },
  methods: {
    subscribeToComments() {
      if (this.unsubscribeComments) this.unsubscribeComments();
      this.comments = [];
      this.loadError = '';
      this.loading = true;

      const commentsRef = collection(db, 'pageComments', this.pageId, 'comments');
      this.unsubscribeComments = onSnapshot(commentsRef, (snapshot) => {
        this.comments = snapshot.docs
          .map((comment) => ({ id: comment.id, ...comment.data() }))
          .sort((left, right) => {
            const leftTime = left.createdAt?.toMillis?.() || 0;
            const rightTime = right.createdAt?.toMillis?.() || 0;
            return leftTime - rightTime;
          });
        this.loading = false;
      }, () => {
        this.loadError = 'The conversation could not be loaded. Please try again shortly.';
        this.loading = false;
      });
    },
    async signIn() {
      this.authenticating = true;
      this.submitError = '';
      try {
        await this.$store.dispatch('signInWithGoogle');
      } catch (error) {
        this.submitError = this.authError || 'Google sign-in did not complete. Please try again.';
      } finally {
        this.authenticating = false;
      }
    },
    async signOutUser() {
      this.submitError = '';

      try {
        await this.$store.dispatch('signOut');
        this.cancelReply();
      } catch (error) {
        this.submitError = this.authError || 'Sign out did not complete. Please try again.';
      }
    },
    startReply(comment) {
      if (!this.user) {
        this.signIn();
        return;
      }

      this.replyTarget = comment;
      this.$nextTick(() => document.getElementById('comment-body')?.focus());
    },
    cancelReply() {
      this.replyTarget = null;
    },
    async submitComment() {
      const body = this.draft.trim();
      if (!body || !this.user || this.submitting) return;

      this.submitting = true;
      this.submitError = '';
      try {
        await addDoc(collection(db, 'pageComments', this.pageId, 'comments'), {
          authorName: this.user.displayName || this.user.email || 'Reckon Guest',
          authorPhoto: this.user.photoURL || null,
          authorUid: this.user.uid,
          body,
          createdAt: serverTimestamp(),
          pagePath: this.pageKey,
          parentId: this.replyTarget?.id || null,
        });
        this.draft = '';
        this.cancelReply();
      } catch (error) {
        this.submitError = 'That comment did not post. Please try again.';
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.comments {
  background: #080808;
  border-top: 1px solid rgba(255, 255, 255, .1);
  color: #fff;
  padding: 68px 24px 84px;
}

.comments__inner {
  margin: 0 auto;
  max-width: 820px;
}

.comments__kicker {
  color: #e85a5a;
  font-size: .72rem;
  font-weight: 900;
  letter-spacing: .14em;
  margin: 0 0 6px;
  text-transform: uppercase;
}

.comments__heading {
  align-items: end;
  display: flex;
  gap: 24px;
  justify-content: space-between;
}

.comments__heading h2 {
  color: #fff;
  font-size: clamp(1.8rem, 4vw, 2.6rem);
  margin: 0;
}

.comments__heading p {
  color: #aaa;
  margin: 8px 0 0;
}

.comments__count {
  color: #aaa;
  font-size: .82rem;
  white-space: nowrap;
}

.comments__composer {
  background: rgba(255, 255, 255, .045);
  border: 1px solid rgba(255, 255, 255, .14);
  border-radius: 12px;
  margin-top: 30px;
  padding: 20px;
}

.comments__composer > p {
  color: #d0d0d0;
  margin: 0 0 14px;
}

.comments__identity,
.comments__actions,
.comments__replying {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.comments__identity {
  color: #aaa;
  font-size: .82rem;
  margin-bottom: 12px;
}

.comments__identity strong {
  color: #fff;
}

.comments__identity button,
.comments__replying button {
  background: none;
  border: 0;
  color: #ef7777;
  cursor: pointer;
  font: inherit;
}

.comments__replying {
  background: rgba(232, 90, 90, .12);
  border-radius: 6px;
  color: #efaaaa;
  font-size: .82rem;
  margin-bottom: 10px;
  padding: 7px 10px;
}

.comments textarea {
  background: #0d0d0d;
  border: 1px solid rgba(255, 255, 255, .2);
  border-radius: 8px;
  color: #fff;
  font: inherit;
  line-height: 1.55;
  padding: 14px;
  resize: vertical;
  width: 100%;
}

.comments textarea:focus {
  border-color: #e85a5a;
  outline: 2px solid rgba(232, 90, 90, .18);
}

.comments__actions {
  margin-top: 12px;
}

.comments__actions small {
  color: #777;
}

.comments__submit,
.comments__signin {
  background: #e23232;
  border: 0;
  border-radius: 6px;
  color: #fff;
  cursor: pointer;
  font: inherit;
  font-weight: 900;
  padding: 10px 16px;
}

.comments__submit:disabled,
.comments__signin:disabled {
  cursor: not-allowed;
  opacity: .48;
}

.comments__notice,
.comments__empty {
  color: #999;
  margin-top: 28px;
}

.comments__notice--error {
  color: #ff9d9d;
}

.comments__empty {
  border-top: 1px solid rgba(255, 255, 255, .1);
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 26px;
}

.comments__empty strong {
  color: #ddd;
}

@media (max-width: 600px) {
  .comments {
    padding: 52px 16px 64px;
  }

  .comments__heading {
    align-items: start;
    flex-direction: column;
    gap: 10px;
  }
}
</style>
